import { acquireMicrophone, isTrackUsable } from './audioDevices';

/**
 * Opening the microphone while the phone is RINGING, not when it is answered.
 *
 * The SDK asks for the microphone inside `call.answer()` — `_retrieveLocalStream`
 * runs a `getUserMedia` (plus, for a pinned device, an `enumerateDevices` pass to
 * resolve the id) before it will build the peer connection, and the SIP 200 OK
 * goes out after that. Measured on a real answer: ~190ms of the ~900ms between
 * the agent clicking Answer and audio flowing, spent before any signalling had
 * happened at all.
 *
 * None of that work needs a caller on the line. The phone rings for seconds
 * first, and the microphone the answer is going to want is knowable then. So it
 * is opened during the ring and handed to the SDK at answer time:
 * `_retrieveLocalStream` starts with "if `options.localStream` is a MediaStream,
 * return it", so a stream put there is used verbatim and the `getUserMedia`
 * never runs.
 *
 * ── What this deliberately does NOT do ──────────────────────────────────────
 *
 * It never prompts. The store only warms when the permission is already
 * `granted`, because a permission bubble raised by a ringing call is the exact
 * failure {@link ensureMicrophoneAccess} exists to prevent — and a bubble that
 * appears *without* anyone clicking Answer is worse, since there is nothing on
 * screen explaining it.
 *
 * It holds the device open for the length of the ring, so the tab's recording
 * indicator lights up while the phone rings rather than when it is answered.
 * That is the cost of the fix, and it is bounded: the stream is released as soon
 * as nothing is ringing any more, whether the call was answered, declined, or
 * the caller gave up.
 */

interface WarmMicrophone {
  stream: MediaStream;
  /** The preference it was opened FOR — `null` meaning "follow the system
   *  default" — not the device it landed on. This is compared against the
   *  preference at answer time, so a mic picked while the phone was ringing
   *  still wins: the warm stream is discarded and the SDK resolves the new
   *  choice itself. */
  micId: string | null;
}

/** The one unclaimed stream, if we have it. Only ever holds a stream no call
 *  has taken: {@link claimWarmMicrophone} empties the slot as it hands it over,
 *  so nothing can release a microphone that is carrying a live call. */
let warm: WarmMicrophone | null = null;

/**
 * Which acquisition attempt is current.
 *
 * `getUserMedia` is async, and the ring it was started for can be over before it
 * resolves — the caller hung up, or the agent answered and the SDK went and got
 * its own. A resolved stream that nobody is waiting for any more is a live
 * microphone with no owner and no way to reach it, so an attempt that finds the
 * epoch has moved on stops its own stream instead of storing it.
 */
let epoch = 0;

/** The attempt in flight, so a second ring does not open the device twice. */
let inFlight: number | null = null;

function stop(stream: MediaStream): void {
  try {
    stream.getTracks().forEach((track) => track.stop());
  } catch {
    // Already stopped — nothing to release.
  }
}

/** Whether a warmed stream can still carry a call. A device that was unplugged
 *  during the ring leaves an `ended` track behind, and handing that to the SDK
 *  would connect the call to a microphone that is not there. */
function usable(stream: MediaStream): boolean {
  try {
    const tracks = stream.getAudioTracks();
    return tracks.length > 0 && tracks.every(isTrackUsable);
  } catch {
    return false;
  }
}

/**
 * Start opening `micId` if it is not open already.
 *
 * Fire-and-forget by design: nothing waits on it and a failure costs nothing —
 * the answer path just falls back to letting the SDK acquire the microphone the
 * way it always did.
 *
 * ── WHY A PINNED DEVICE IS OPENED `exact`-ONLY ─────────────────────────────
 *
 * A pinned device id is the weak half of the preference: Chrome regenerates it
 * when a headset re-enumerates, and the LABEL is what survives. The SDK knows
 * that — `answer()` resolves `micId`/`micLabel` by walking the device list and
 * taking the first entry whose id OR label matches, so a re-plugged headset is
 * found again under its new id.
 *
 * `acquireMicrophone` normally retries a failed `exact` as `ideal`, which Chrome
 * is free to read as "give them the default". That is the right trade when the
 * alternative is a call with no microphone at all, and the wrong one here: it
 * would warm the laptop mic, store it under the pinned id, and hand it over at
 * answer time as though it were the headset — pre-empting the label recovery
 * with a stream from the very device the agent chose not to use.
 *
 * So a pinned id that cannot be opened exactly is not warmed at all, and the
 * answer falls through to the SDK's resolution. One slow answer beats a call on
 * the wrong microphone.
 */
export function warmMicrophone(micId: string | null): void {
  if (inFlight !== null) return;
  if (warm) {
    // Same device and still alive: already done. Anything else — a different
    // choice, or a device that died during the ring — is released and reopened.
    if (warm.micId === micId && usable(warm.stream)) return;
    releaseWarmMicrophone();
  }

  const attempt = ++epoch;
  inFlight = attempt;
  void acquireMicrophone(micId, { exactOnly: true })
    .then((stream) => {
      if (epoch !== attempt) {
        stop(stream);
        return;
      }
      warm = { stream, micId };
    })
    .catch(() => {
      // No microphone, device busy, permission revoked mid-ring, or a pinned id
      // that has gone stale — all of them mean "don't warm", never "don't
      // answer".
    })
    .finally(() => {
      if (inFlight === attempt) inFlight = null;
    });
}

/**
 * Take the warmed stream for a call about to be answered on `micId`.
 *
 * Returns `null` whenever the warm stream is not the right one to use — nothing
 * warmed in time, the agent changed microphone while the phone rang, or the
 * device went away — and the caller then answers exactly as it did before.
 */
export function claimWarmMicrophone(micId: string | null): MediaStream | null {
  const held = warm;
  if (!held) return null;
  // Out of the slot either way: from here it is the call's stream to release,
  // or ours to stop, but never something a later release could take back.
  warm = null;
  if (held.micId !== micId || !usable(held.stream)) {
    stop(held.stream);
    return null;
  }
  return held.stream;
}

/** Give the device back. Safe to call when nothing is warm, and it also voids
 *  an acquisition still in flight — otherwise a `getUserMedia` started for a
 *  ring that is already over would resolve into an unreachable open device. */
export function releaseWarmMicrophone(): void {
  epoch += 1;
  inFlight = null;
  const held = warm;
  warm = null;
  if (held) stop(held.stream);
}

export const __testing = {
  /** The unclaimed stream, for assertions. Never read by the app. */
  peek: (): MediaStream | null => warm?.stream ?? null,
  reset: (): void => releaseWarmMicrophone(),
};
